/**
 * PersonaPromptsTab Component
 * 
 * Displays and manages AI persona prompts (grade-independent).
 * Teachers can view, edit, and reset persona prompts for each persona type.
 * 
 * Layout: 4 personas with single prompt each (no grade levels)
 */

import React, { useState, useEffect } from 'react';
import { getApiUrl } from '../../utils/apiUtils';
import PromptEditorModal from './PromptEditorModal';

// Types
interface PersonaPrompt {
  id: number;
  persona_type: string;
  grade_level: string;
  prompt_text: string;
  is_default: boolean;
  created_at: string;
  updated_at: string;
}

interface PersonaPromptsTabProps {
  onError?: (message: string) => void;
  onSuccess?: (message: string) => void;
}

// Constants
const PERSONAS = [
  { key: 'gentle_teacher', name: 'Gentle Teacher', icon: '👨‍🏫', color: 'bg-green-100' },
  { key: 'pronunciation_coach', name: 'Pronunciation Coach', icon: '🗣️', color: 'bg-blue-100' },
  { key: 'quiz_critter', name: 'Quiz Critter', icon: '🦊', color: 'bg-purple-100' },
  { key: 'role_ranger', name: 'Role Ranger', icon: '🎭', color: 'bg-orange-100' },
];

export default function PersonaPromptsTab({ onError, onSuccess }: PersonaPromptsTabProps) {
  const [prompts, setPrompts] = useState<PersonaPrompt[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedPrompt, setSelectedPrompt] = useState<PersonaPrompt | null>(null);
  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  // Load all prompts on component mount
  useEffect(() => {
    loadPrompts();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshTrigger]);

  const loadPrompts = async () => {
    try {
      setLoading(true);
      const response = await fetch(getApiUrl('/v1/dashboard/persona-prompts'), {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to load prompts: ${response.statusText}`);
      }

      const data = await response.json();
      
      // Backend returns structure: { prompts: { gentle_teacher: {...}, pronunciation_coach: {...}, ... } }
      // Convert to flat array for easier access
      const promptsArray: PersonaPrompt[] = [];
      
      if (data.prompts && typeof data.prompts === 'object') {
        Object.entries(data.prompts).forEach(([personaType, prompt]) => {
          if (prompt && typeof prompt === 'object') {
            const p = prompt as any;
            promptsArray.push({
              id: p.id,
              persona_type: p.persona_type || personaType,
              grade_level: p.grade_level || 'all',
              prompt_text: p.prompt_template || p.prompt_text || '',
              is_default: p.is_default ?? true,
              created_at: p.created_at || '',
              updated_at: p.updated_at || ''
            });
          }
        });
      }
      
      setPrompts(promptsArray);
    } catch (error) {
      console.error('Error loading persona prompts:', error);
      onError?.('Failed to load persona prompts. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const getPromptForPersona = (personaKey: string): PersonaPrompt | null => {
    return prompts.find(p => p.persona_type === personaKey) || null;
  };

  const handleEditPrompt = (personaKey: string) => {
    const prompt = getPromptForPersona(personaKey);
    if (prompt) {
      setSelectedPrompt(prompt);
      setIsEditorOpen(true);
    }
  };

  const handlePromptSaved = () => {
    setIsEditorOpen(false);
    setSelectedPrompt(null);
    setRefreshTrigger(prev => prev + 1); // Trigger reload
    onSuccess?.('Prompt updated successfully!');
  };

  const handleResetToDefault = async (personaKey: string) => {
    if (!window.confirm(`Reset ${personaKey} prompt to default?`)) {
      return;
    }

    try {
      const response = await fetch(
        getApiUrl(`/v1/dashboard/persona-prompts/${personaKey}/all/reset`),
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to reset prompt: ${response.statusText}`);
      }

      setRefreshTrigger(prev => prev + 1); // Trigger reload
      onSuccess?.(`Reset ${personaKey} prompt to default.`);
    } catch (error) {
      console.error('Error resetting prompt:', error);
      onError?.('Failed to reset prompt. Please try again.');
    }
  };

  const handleResetAll = async () => {
    if (!window.confirm('Reset ALL persona prompts to defaults? This cannot be undone.')) {
      return;
    }

    try {
      const response = await fetch(
        getApiUrl('/v1/dashboard/persona-prompts/reset-all'),
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to reset all prompts: ${response.statusText}`);
      }

      setRefreshTrigger(prev => prev + 1); // Trigger reload
      onSuccess?.('All prompts reset to defaults successfully!');
    } catch (error) {
      console.error('Error resetting all prompts:', error);
      onError?.('Failed to reset all prompts. Please try again.');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading persona prompts...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
              <span>🎨</span>
              Persona Prompts Configuration
            </h2>
            <p className="text-sm text-gray-600 mt-2">
              Customize AI persona behavior. Each persona has a single prompt that applies to all grade levels.
            </p>
          </div>
          <button
            onClick={handleResetAll}
            className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors text-sm font-medium"
          >
            Reset All to Defaults
          </button>
        </div>
      </div>

      {/* Prompts List */}
      <div className="space-y-4">
        {PERSONAS.map((persona) => {
          const prompt = getPromptForPersona(persona.key);
          const isDefault = prompt?.is_default ?? true;
          const promptPreview = prompt?.prompt_text
            ? prompt.prompt_text.substring(0, 150) + (prompt.prompt_text.length > 150 ? '...' : '')
            : 'No prompt found';

          return (
            <div
              key={persona.key}
              className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow"
            >
              <div className="flex items-start gap-4">
                {/* Persona Icon and Name */}
                <div className="flex-shrink-0">
                  <div className={`${persona.color} rounded-lg p-4`}>
                    <span className="text-4xl">{persona.icon}</span>
                  </div>
                </div>

                {/* Prompt Details */}
                <div className="flex-grow">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-xl font-semibold text-gray-800">{persona.name}</h3>
                    <div className="flex items-center gap-2">
                      {!isDefault && (
                        <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs font-semibold rounded">
                          Custom
                        </span>
                      )}
                      <span className="text-xs text-gray-500">
                        {prompt?.prompt_text ? `${prompt.prompt_text.split(' ').length} words` : 'N/A'}
                      </span>
                    </div>
                  </div>
                  
                  <p className="text-sm text-gray-600 mb-4 leading-relaxed">
                    {promptPreview}
                  </p>

                  {/* Action Buttons */}
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleEditPrompt(persona.key)}
                      className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors text-sm font-medium"
                    >
                      ✏️ Edit Prompt
                    </button>
                    {!isDefault && (
                      <button
                        onClick={() => handleResetToDefault(persona.key)}
                        className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition-colors text-sm font-medium"
                      >
                        🔄 Reset to Default
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Info Panel */}
      <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
        <h3 className="font-semibold text-blue-900 mb-2 flex items-center gap-2">
          <span>ℹ️</span>
          About Persona Prompts
        </h3>
        <ul className="text-sm text-blue-800 space-y-1">
          <li>• <strong>Grade-Independent:</strong> Each persona uses a single prompt that works for all grade levels</li>
          <li>• <strong>Template Variables:</strong> Prompts use variables like {'{child_age}'}, {'{story_title}'}, {'{difficult_word}'} for dynamic content</li>
          <li>• <strong>Customizable:</strong> Edit any prompt to match your teaching style or curriculum needs</li>
          <li>• <strong>Resettable:</strong> Restore default prompts at any time (individually or all at once)</li>
        </ul>
      </div>

      {/* Prompt Editor Modal */}
      {isEditorOpen && selectedPrompt && (
        <PromptEditorModal
          prompt={selectedPrompt}
          onClose={() => {
            setIsEditorOpen(false);
            setSelectedPrompt(null);
          }}
          onSave={handlePromptSaved}
          onError={onError}
          onSuccess={onSuccess}
        />
      )}
    </div>
  );
}
